/**
 * AdminApprovalsPage — admin-only queue of pending Editor actions.
 *
 * Shows every staged action (gallery delete, bulk delete, event delete,
 * photo replace, album delete) with ARIA's risk score, the Editor's
 * intent, and a one-click approve / reject. Admin notes are captured
 * for the audit trail.
 *
 * Auto-refreshes every 15 seconds so a fresh Editor request appears
 * without a manual reload. Stops polling when nothing's pending.
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShieldAlert, ShieldCheck, ShieldX, Clock, AlertTriangle,
  Sparkles, ChevronRight, RefreshCw, ChevronLeft, Inbox, Users,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { errorMessage } from "@/lib/errors";

const RISK_STYLES = {
  high: "border-red-500/50 bg-red-500/10 text-red-300",
  medium: "border-amber-500/40 bg-amber-500/10 text-amber-300",
  low: "border-green-500/30 bg-green-500/5 text-green-300",
  pending: "border-white/20 bg-white/5 text-white/60",
};

const ACTION_KIND_LABELS = {
  "gallery.delete": "Delete Gallery",
  "event.delete": "Delete Event",
  "bulk.delete": "Bulk Delete Photos",
  "photo.replace": "Replace Photo",
  "album.delete": "Delete Album",
};

function timeAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "just now";
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
}

function RiskBadge({ level, reason }) {
  const cls = RISK_STYLES[level] || RISK_STYLES.pending;
  const Icon = level === "high" ? AlertTriangle : level === "medium" ? ShieldAlert : ShieldCheck;
  return (
    <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 border text-[10px] uppercase tracking-wider font-body ${cls}`} title={reason}>
      <Icon size={11} />
      <span>{level === "pending" ? "scoring…" : level}</span>
    </div>
  );
}

function ApprovalRow({ item, onDecided }) {
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState("");
  const [expanded, setExpanded] = useState(false);

  const decide = async (action) => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await api.post(`/admin/approvals/${item.id}/${action}`, { notes });
      onDecided(item.id, res.data);
      toast.success(
        action === "approve"
          ? `Approved — ${item.target_label || item.action_kind} executed.`
          : `Rejected — Editor will be notified.`,
      );
    } catch (err) {
      toast.error(errorMessage(err, `Could not ${action} request.`));
    } finally {
      setBusy(false);
    }
  };

  const beforeSummary = useMemo(() => {
    const b = item.before_snapshot;
    if (!b) return null;
    const parts = [];
    if (b.title) parts.push(`Title: "${b.title}"`);
    if (b.event_type) parts.push(`Type: ${b.event_type}`);
    if (typeof b.media_count === "number") parts.push(`Media count: ${b.media_count}`);
    if (b.event_date) parts.push(`Date: ${b.event_date}`);
    if (b.is_published) parts.push("PUBLISHED");
    if (b.count) parts.push(`Photos selected: ${b.count}`);
    return parts;
  }, [item]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="bg-luxury-charcoal/60 border border-white/10 hover:border-gold/30 transition-colors p-5 mb-3"
      data-testid={`approval-row-${item.id}`}
    >
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <span className="text-gold font-heading text-sm tracking-wide" data-testid={`approval-action-${item.id}`}>
              {ACTION_KIND_LABELS[item.action_kind] || item.action_kind}
            </span>
            <RiskBadge level={item.risk_level} reason={item.risk_reason} />
            <span className="text-white/30 text-[10px] font-body inline-flex items-center gap-1">
              <Clock size={10} /> {timeAgo(item.created_at)}
            </span>
          </div>
          <p className="text-white text-sm font-body break-words" data-testid={`approval-target-${item.id}`}>
            {item.target_label || <span className="text-white/40 italic">untitled</span>}
          </p>
          <p className="text-white/50 text-xs font-body mt-1">
            Requested by{" "}
            <span className="text-gold/80" data-testid={`approval-actor-${item.id}`}>
              {item.actor_email}
            </span>
          </p>
        </div>
      </div>

      {/* ARIA reasoning */}
      {item.risk_reason && (
        <div className="bg-luxury-black/60 border-l-2 border-gold/40 pl-3 py-2 mb-3 flex items-start gap-2">
          <Sparkles size={12} className="text-gold/70 mt-0.5 shrink-0" />
          <p className="text-white/70 text-xs font-body italic leading-relaxed">
            <span className="text-gold/80 font-semibold">ARIA · </span>{item.risk_reason}
          </p>
        </div>
      )}

      {/* Before-state preview (collapsible) */}
      {beforeSummary && beforeSummary.length > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="text-white/40 hover:text-gold text-[11px] font-body flex items-center gap-1 mb-2"
          data-testid={`approval-expand-${item.id}`}
        >
          <ChevronRight size={11} className={`transition-transform ${expanded ? "rotate-90" : ""}`} />
          {expanded ? "Hide" : "Show"} target details
        </button>
      )}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <ul className="text-white/60 text-xs font-body space-y-0.5 pl-5 mb-3">
              {beforeSummary.map((p, i) => <li key={i}>· {p}</li>)}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Notes input + decision buttons */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-2 items-stretch">
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional notes (recorded in audit log)…"
          className="bg-luxury-black/60 border border-white/10 text-white text-xs px-3 py-2 placeholder:text-white/30 focus:border-gold/40 outline-none font-body"
          data-testid={`approval-notes-${item.id}`}
          maxLength={500}
        />
        <button
          type="button"
          onClick={() => decide("approve")}
          disabled={busy}
          className="flex items-center justify-center gap-1.5 bg-green-500/15 hover:bg-green-500/25 text-green-300 border border-green-500/30 px-4 py-2 text-xs font-body uppercase tracking-wider transition-colors disabled:opacity-40"
          data-testid={`approval-approve-${item.id}`}
        >
          <ShieldCheck size={12} /> Approve
        </button>
        <button
          type="button"
          onClick={() => decide("reject")}
          disabled={busy}
          className="flex items-center justify-center gap-1.5 bg-red-500/15 hover:bg-red-500/25 text-red-300 border border-red-500/30 px-4 py-2 text-xs font-body uppercase tracking-wider transition-colors disabled:opacity-40"
          data-testid={`approval-reject-${item.id}`}
        >
          <ShieldX size={12} /> Reject
        </button>
      </div>
    </motion.div>
  );
}

function EditorTrustPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/admin/editor-trust")
      .then((res) => {
        if (cancelled) return;
        // Bake cooldownDays into each row at fetch time so the
        // render path stays pure (no Date.now() during JSX).
        const now = Date.now();
        const enriched = (res.data.items || []).map((r) => ({
          ...r,
          cooldownDays: r.cooldown_until
            ? Math.max(0, Math.ceil((new Date(r.cooldown_until).getTime() - now) / 86400000))
            : 0,
        }));
        setRows(enriched);
      })
      .catch(() => { /* silent */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading || rows.length === 0) return null;
  const trusted = rows.filter((r) => r.tier === "trusted").length;
  const novice = rows.length - trusted;

  return (
    <div className="mb-6 bg-luxury-charcoal/40 border border-white/10 p-4" data-testid="editor-trust-panel">
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="w-full flex items-center justify-between gap-2 mb-3"
        data-testid="editor-trust-toggle"
      >
        <div className="flex items-center gap-2">
          <Users size={14} className="text-gold/70" />
          <span className="text-gold/90 font-heading text-sm tracking-wider">Editor Trust</span>
          <span className="text-white/40 text-[10px] font-body">
            {trusted} trusted · {novice} novice
          </span>
        </div>
        <ChevronRight size={12} className={`text-white/40 transition-transform ${collapsed ? "" : "rotate-90"}`} />
      </button>
      {!collapsed && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {rows.map((r) => {
            const cls = r.cooldownDays > 0
              ? "border-amber-500/30 bg-amber-500/5"
              : r.tier === "trusted"
                ? "border-gold/30 bg-gold/5"
                : "border-white/10 bg-white/[0.02]";
            return (
              <div
                key={r.email}
                className={`flex items-center justify-between gap-3 px-3 py-2 border ${cls}`}
                data-testid={`trust-row-${r.email}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-white text-xs truncate font-body">{r.name || r.email}</p>
                  <p className="text-white/40 text-[10px] font-body">
                    {r.approved_count} approved · {r.rejected_count} rejected
                  </p>
                </div>
                {r.cooldownDays > 0 ? (
                  <span className="text-amber-300 text-[10px] uppercase tracking-wider font-body">
                    cooldown {r.cooldownDays}d
                  </span>
                ) : r.tier === "trusted" ? (
                  <span className="inline-flex items-center gap-1 text-gold text-[10px] uppercase tracking-wider font-body">
                    <Sparkles size={9} /> trusted
                  </span>
                ) : (
                  <span className="text-white/40 text-[10px] uppercase tracking-wider font-body">
                    novice {r.approved_count}/{r.approved_count + r.next_threshold}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function AdminApprovalsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending"); // pending | approved | rejected | all
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/approvals?status=${statusFilter}&limit=200`);
      setItems(res.data.items || []);
      setLastRefresh(new Date());
    } catch (err) {
      toast.error(errorMessage(err, "Could not load approval queue."));
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  // Auto-refresh every 15s while looking at pending — keeps the
  // queue live without straining the LLM (the risk-score endpoint
  // is the heaviest write path and only fires on submit, never on
  // poll).
  useEffect(() => {
    if (statusFilter !== "pending") return undefined;
    const id = setInterval(() => { load(); }, 15000);
    return () => clearInterval(id);
  }, [statusFilter, load]);

  const handleDecided = useCallback((id, updated) => {
    // Re-fetch instead of mutating in place so risk_level / decided_by
    // stay accurate even if the row moved across status buckets.
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const counts = useMemo(() => {
    const by = { high: 0, medium: 0, low: 0, pending: 0 };
    for (const i of items) by[i.risk_level || "pending"] = (by[i.risk_level || "pending"] || 0) + 1;
    return by;
  }, [items]);

  return (
    <div className="min-h-screen bg-luxury-black pt-24 pb-16 px-6 md:px-12">
      <div className="max-w-5xl mx-auto" data-testid="admin-approvals-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <button
              type="button"
              onClick={() => navigate("/admin/dashboard")}
              className="text-white/40 hover:text-gold text-xs font-body inline-flex items-center gap-1 mb-2"
              data-testid="approvals-back-btn"
            >
              <ChevronLeft size={12} /> Back to Dashboard
            </button>
            <h1 className="font-heading text-3xl md:text-4xl text-white tracking-tight">
              Pending <span className="text-gold">Approvals</span>
            </h1>
            <p className="text-white/40 text-xs font-body mt-2">
              Editor-initiated destructive actions awaiting your review. ARIA scores each request by risk.
              {lastRefresh && (
                <span className="ml-2 text-white/30">· last refresh {timeAgo(lastRefresh.toISOString())}</span>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            className="bg-gold/15 hover:bg-gold/25 text-gold px-4 py-2 text-xs font-body uppercase tracking-wider flex items-center gap-1.5 transition-colors"
            data-testid="approvals-refresh-btn"
          >
            <RefreshCw size={12} /> Refresh
          </button>
        </div>

        {/* Status filter pills */}
        <EditorTrustPanel />
        <div className="flex flex-wrap gap-2 mb-6">
          {[
            { v: "pending", label: "Pending" },
            { v: "approved", label: "Approved" },
            { v: "rejected", label: "Rejected" },
            { v: "all", label: "All" },
          ].map((p) => (
            <button
              key={p.v}
              type="button"
              onClick={() => setStatusFilter(p.v)}
              className={`px-4 py-1.5 text-[11px] font-body uppercase tracking-wider border transition-colors ${
                statusFilter === p.v
                  ? "bg-gold/20 text-gold border-gold/40"
                  : "bg-luxury-charcoal/40 text-white/50 border-white/10 hover:border-gold/30"
              }`}
              data-testid={`approvals-filter-${p.v}`}
            >
              {p.label}
            </button>
          ))}
          {statusFilter === "pending" && items.length > 0 && (
            <div className="ml-auto flex items-center gap-2">
              {counts.high > 0 && <span className="text-red-300 text-xs">{counts.high} high</span>}
              {counts.medium > 0 && <span className="text-amber-300 text-xs">{counts.medium} medium</span>}
              {counts.low > 0 && <span className="text-green-300 text-xs">{counts.low} low</span>}
            </div>
          )}
        </div>

        {/* List */}
        {loading ? (
          <div className="text-white/40 text-center py-16 font-body text-sm" data-testid="approvals-loading">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-white/10" data-testid="approvals-empty">
            <Inbox size={48} className="text-white/15 mx-auto mb-3" />
            <p className="text-white/50 font-body text-sm">
              {statusFilter === "pending"
                ? "Nothing waiting on you. Editors are working safely."
                : `No ${statusFilter} requests.`}
            </p>
          </div>
        ) : (
          <AnimatePresence>
            {items.map((item) => (
              <ApprovalRow key={item.id} item={item} onDecided={handleDecided} />
            ))}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
}
